/**
 * A helper class to make request to API server (Java backend)
 * The API may need authentication, in which case it will use the authentication token
 * from our centralized auth system in Authorization: Bearer xxx
 */
import axios from 'axios';

import { apiConfig } from '@/config/api';
import { dispatchQuotaError } from '@/lib/global-events';
import { getActiveProfileId } from '@/lib/active-profile';

// Use the centralized API configuration
const API_BASE_URL = apiConfig.baseUrl;

// Create axios instance with configurable base URL
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // IMPORTANT: Send cookies with cross-domain requests
});

// Create a separate, unauthenticated client for public endpoints
export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Prevent multiple simultaneous redirects
let isRedirectingToSignin = false;

// Add request interceptor for logging/debugging if needed, but no longer for Auth headers
apiClient.interceptors.request.use(
  async (config) => {
    // Inject active profile ID header so the backend uses the right AI persona
    const profileId = getActiveProfileId();
    if (profileId) {
      config.headers['X-Profile-ID'] = profileId;
    }
    return config;
  },
  (error) => {
    console.error('[API Client] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // console.log('[API Client] Response received:', {
    //   status: response.status,
    //   url: response.config.url,
    //   data: response.data
    // });
    return response;
  },
  (error) => {
    // console.error('[API Client] Response error:', {
    //   url: error.config?.url,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   message: error.message
    // });

    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          // Only redirect if we're not already on the signin page
          // and not on public auth routes (avoid breaking reset/forgot flows)
          {
            const path = window.location.pathname;
            const isPublicAuthRoute = (
              path === '/forgot-password' ||
              path === '/reset-password' ||
              path.startsWith('/auth/')
            );
            if (!path.includes('/signin') && !isPublicAuthRoute) {
              console.log('######## 401 error got to request ' + path);
              window.location.href = `${apiConfig.wwwUrl}/signin/`;
            }
          }
          break;
        case 402:
          // Handle quota exceeded
          if (error.response.data?.message) {
            dispatchQuotaError({ message: error.response.data.message });
          }
          break;
        case 403:
          // Handle forbidden
          console.error('Access forbidden');
          break;
        case 404:
          // Handle not found
          console.error('Resource not found');
          break;
        default:
          // Handle other errors
          console.error('API error:', error.response.data);
      }
    }
    return Promise.reject(error);
  }
);
